import React from "react";
import { useParams } from "react-router-dom";
import Navbar from "./navbar";
import Footer from "./footer";
import IndustryData from "./industrydata";
import {
  CheckCircle,
  BarChart3,
  Shield,
  Zap,
  Users,
  PieChart,
  Smartphone,
  CreditCard,
  Cloud,
  TrendingUp,
  ArrowRight,
  Lock,
  Globe,
} from "lucide-react";
import "./industry.css";

const Industry = () => {
  const { id } = useParams();
  const industry = IndustryData.find((ind) => ind.id === id);

  if (!industry)
    return (
      <div className="not-found-container">
        <Navbar />
        <div className="not-found-content">
          <h1>Industry not found</h1>
          <p>The industry solution you're looking for doesn't exist.</p>
          <a href="/" className="btn-primary">
            Back to Home
          </a>
        </div>
        <Footer />
      </div>
    );

  // Mock icons for capabilities if not provided
  const defaultIcons = [
    <BarChart3 />,
    <Shield />,
    <Zap />,
    <Users />,
    <PieChart />,
    <Smartphone />,
  ];

  return (
    <>
      <Navbar />
      <div style={{ paddingTop: "80px" }}>
        {/* HERO SECTION */}
        <section className="industry-section position-relative">
          <div className="industry-gradient-overlay"></div>

          <div className="container">
            <div className="row align-items-center">
              {/* TEXT CONTENT */}
              <div
                className="col-md-6 order-2 order-md-1
                      text-center text-md-start"
              >
                {industry.badge && (
                  <div className="badge-wrapper my-4 mt-md-0  mb-md-3">
                    <span className="feature-tag">{industry.badge}</span>
                  </div>
                )}

                <h1
                  className="industry-title"
                  style={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                  <span className="gradient-text">{industry.name}</span>{" "}
                  Solutions
                </h1>

                <p className="industry-subtitle">
                  Advanced billing & inventory management tailored for the{" "}
                  {industry.name.toLowerCase()} industry
                </p>

                <p className="industry-description">{industry.description}</p>

                <div
                  className="industry-buttons d-flex
                        justify-content-center justify-content-md-start gap-3"
                >
                  <a href="/login">
                    <button className="cta-btn cta-btn-primary">
                      Start Free Trial <ArrowRight size={18} />
                    </button>
                  </a>
                </div>

                {/* Trust indicators */}
                <div className="trust-indicators d-none d-md-flex gap-4">
                  <div className="trust-item">
                    <div className="trust-icon">✓</div>
                    <span>No credit card required</span>
                  </div>
                  <div className="trust-item">
                    <div className="trust-icon">✓</div>
                    <span>14-day free trial</span>
                  </div>
                  <div className="trust-item">
                    <div className="trust-icon">✓</div>
                    <span>24/7 support</span>
                  </div>
                </div>
              </div>

              {/* IMAGE */}
              <div
                className="col-md-6 order-1 order-md-2 mb-4 mb-md-0
                      text-center position-relative"
              >
                <img
                  src={industry.image}
                  alt={industry.title}
                  className="img-fluid"
                />
              </div>
            </div>
          </div>
        </section>

        {/* BENEFITS SECTION */}
        {industry.benefits?.length > 0 && (
          <section className="benefits-section">
            <div className="container">
              <div className="section-header">
                <span className="section-subtitle">Why Choose Us</span>
                <h2 className="industrysection-title">
                  Key Benefits for {industry.name}
                </h2>
                <p className="section-description">
                  Designed to address the specific challenges of your industry
                </p>
              </div>

              <div className="benefits-grid">
                {industry.benefits.map((b, i) => (
                  <div className="benefit-card" key={i}>
                    <div className="benefit-number">0{i + 1}</div>
                    <div className="benefit-icon">
                      <div className="icon-wrapperr">
                        <CheckCircle size={24} />
                      </div>
                    </div>
                    <h3 className="benefit-title">{b.split(":")[0]}</h3>
                    <p className="benefit-description">
                      {b.includes(":")
                        ? b.split(":")[1]
                        : `Streamline operations and increase efficiency with our ${industry.name.toLowerCase()} solutions.`}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FEATURES SECTION */}
        {industry.features?.length > 0 && (
          <section className="features-section py-5">
            <div className="container">
              {/* Section Header */}
              <div className="text-center mb-5">
                <span className="section-subtitle">Tailored Features</span>
                <h2 className="mt-2 fw-bold">
                  Powerful Features for Your Business
                </h2>
              </div>

              {/* Features List */}
              {industry.features.map((f, i) => (
                <div className="row align-items-center mb-5" key={i}>
                  {/* Content */}
                  <div
                    className={`col-md-6 ${
                      i % 2 !== 0 ? "order-md-2" : ""
                    } mb-4 mb-md-0`}
                  >
                    <div className="feature-content">
                      <div className="feature-tag">Feature {i + 1}</div>
                      <h3 className="fw-bold">{f.title}</h3>
                      <p className="text-secondary">{f.desc}</p>

                      {/* Bullet Points */}
                      {f.points?.length > 0 && (
                        <ul className="list-unstyled mt-3">
                          {f.points.map((point, idx) => (
                            <li
                              className="d-flex align-items-center mb-2"
                              key={idx}
                            >
                              <CheckCircle
                                size={16}
                                className="me-2 text-black"
                              />
                              {point}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* Image */}
                  <div
                    className={`col-md-6 text-center ${
                      i % 2 !== 0 ? "order-md-1" : ""
                    }`}
                  >
                    <img
                      src={f.image}
                      alt={f.title}
                      className="img-fluid rounded"
                      style={{ maxHeight: "400px", objectFit: "contain" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CAPABILITIES SECTION */}
        {industry.capabilities?.length > 0 && (
          <section className="capabilities-section">
            <div className="container">
              <div className="section-header">
                <span className="section-subtitle">Our Technology</span>
                <h2 className="industrysection-title">Core Capabilities</h2>
                <p className="section-description">
                  Built with the latest technology to power your business
                </p>
              </div>

              <div className="capabilities-grid">
                {industry.capabilities.map((c, i) => (
                  <div className="capability-card" key={i}>
                    <div className="capability-icon">
                      <div className="icon-circle">
                        {c.icon || defaultIcons[i] || <TrendingUp size={24} />}
                      </div>
                    </div>
                    <h3 className="capability-title">{c.title}</h3>
                    <p className="capability-description">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* testimonial SECTION */}
        {industry.testimonial && (
          <div className="testimonial-wrapper">
            <div className="testimonial-card">
              {/* <div className="quote-mark">“</div> */}

              <p className="testimonial-text">{industry.testimonial.text}</p>

              <div className="testimonial-author">
                <div className="author-avatar">
                  <img
                    src={industry.testimonial.image}
                    alt={industry.testimonial.title}
                    className="img-fluid rounded-circle"
                  />
                </div>

                <div className="author-info">
                  <strong>{industry.testimonial.author}</strong>
                  <span>
                    {industry.testimonial.position || "Business Owner"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA SECTION */}
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <h2 className="cta-title">
                Ready to transform your {industry.name.toLowerCase()} business?
              </h2>
              <p className="cta-description">
                Join thousands of businesses that trust our platform
              </p>
              <div className="cta-buttons">
                <a href="/login">
                  <button className="btn-white ">
                    Start Your Free Trial <ArrowRight size={18} />
                  </button>
                </a>

                {/* <button className="demo  btn-xl">Schedule a Demo</button> */}
              </div>
              <p className="cta-note">
                No credit card required • Free 14-day trial • Cancel anytime
              </p>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Industry;
